import { getMonthsElapsed, startOfMonthUTC, toDate } from './dates.js';
import type {
  CapacityEventInput,
  CustomerCapacityGroupInput,
  CustomerGrowthScenarioInput,
  CustomerProjectionInput,
} from './types.js';

export interface ProjectionSimulationState {
  customers: number;
  constrained: boolean;
  grossAcquisitions: number;
  churned: number;
}

export interface MonthSimulationResult {
  byProjection: Map<string, ProjectionSimulationState>;
  groupTotals: Map<string, number>;
}

function projectionKey(projection: CustomerProjectionInput, index: number): string {
  return projection.id ?? `projection-${index}`;
}

function isProjectionActive(
  projection: CustomerProjectionInput,
  monthDate: Date,
): boolean {
  const start = toDate(projection.startDate);
  if (!start) return false;
  const end = projection.endDate ? toDate(projection.endDate) : null;
  const month = startOfMonthUTC(monthDate).getTime();
  if (month < startOfMonthUTC(start).getTime()) return false;
  if (end && month > startOfMonthUTC(end).getTime()) return false;
  return true;
}

function findGrowthScenario(
  projection: CustomerProjectionInput,
  scenarios: CustomerGrowthScenarioInput[],
  monthDate: Date,
): CustomerGrowthScenarioInput | undefined {
  const month = startOfMonthUTC(monthDate).getTime();
  return scenarios.find((scenario) => {
    const start = toDate(scenario.startDate);
    if (!start || month < startOfMonthUTC(start).getTime()) return false;
    const end = scenario.endDate ? toDate(scenario.endDate) : null;
    if (end && month > startOfMonthUTC(end).getTime()) return false;
    if (scenario.appliesTo === 'specific_segments') {
      return (
        projection.id !== undefined &&
        scenario.targetSegmentIds?.includes(projection.id)
      );
    }
    return scenario.appliesTo === 'all_projections';
  });
}

function resolveSaturationPoint(
  scenario?: CustomerGrowthScenarioInput,
): number | undefined {
  if (scenario?.marketSaturationPoint && scenario.marketSaturationPoint > 0) {
    return scenario.marketSaturationPoint;
  }
  return undefined;
}

function applySaturationMultiplier(
  planned: number,
  current: number,
  saturationPoint?: number,
  slowdownRate?: number,
): number {
  if (!saturationPoint || saturationPoint <= 0 || planned <= 0) return planned;
  const fillRatio = Math.min(1, Math.max(0, current / saturationPoint));
  const rate = slowdownRate ?? 1;
  return planned * Math.pow(1 - fillRatio, rate);
}

function getEffectiveGroupMax(
  group: CustomerCapacityGroupInput,
  monthDate: Date,
  events: CapacityEventInput[],
): number {
  let max = group.maxCustomers;
  for (const event of events) {
    if (event.capacityGroupId !== group.id) continue;
    const eventDate = toDate(event.date);
    if (!eventDate || eventDate.getTime() > monthDate.getTime()) continue;
    max += event.capacityIncrease ?? 0;
  }
  return max;
}

function getEffectiveProjectionMax(
  projection: CustomerProjectionInput,
  monthDate: Date,
  events: CapacityEventInput[],
): number | undefined {
  if (!projection.maxCustomers || projection.maxCustomers <= 0) return undefined;
  let max = projection.maxCustomers;
  if (!projection.id) return max;
  for (const event of events) {
    if (event.projectionId !== projection.id) continue;
    const eventDate = toDate(event.date);
    if (!eventDate || eventDate.getTime() > monthDate.getTime()) continue;
    max += event.capacityIncrease ?? 0;
  }
  return max;
}

function plannedGrossAcquisitions(
  projection: CustomerProjectionInput,
  current: number,
  monthsElapsed: number,
): number {
  if (projection.projectionType === 'fixed_monthly') {
    return projection.monthlyAcquisitionTarget ?? 0;
  }

  if (projection.projectionType === 'growth_rate') {
    const growthRate = projection.monthlyGrowthRate ?? 0;
    const monthlyAcquisition = projection.monthlyAcquisitionTarget ?? 0;
    let acquired = current * growthRate + monthlyAcquisition;
    if (current === 0 && acquired === 0 && growthRate > 0) {
      acquired = 1;
    }
    return acquired;
  }

  if (projection.projectionType === 'custom_timeline' && projection.customTimeline?.length) {
    const target = projection.customTimeline.find((entry) => entry.month === monthsElapsed);
    if (target) {
      return Math.max(0, target.expectedCustomers - current);
    }
  }

  return 0;
}

function customersFromCustomTimeline(
  projection: CustomerProjectionInput,
  monthsElapsed: number,
): number {
  if (!projection.customTimeline?.length) {
    return projection.initialCustomers ?? 0;
  }
  const exact = projection.customTimeline.find((entry) => entry.month === monthsElapsed);
  if (exact) return exact.expectedCustomers;
  const sorted = [...projection.customTimeline].sort((a, b) => a.month - b.month);
  const last = [...sorted].reverse().find((entry) => entry.month <= monthsElapsed);
  return last ? last.expectedCustomers : projection.initialCustomers ?? 0;
}

export function simulateProjectionsToMonth(
  projections: CustomerProjectionInput[],
  monthsElapsed: number,
  options: {
    capacityGroups?: CustomerCapacityGroupInput[];
    capacityEvents?: CapacityEventInput[];
    growthScenarios?: CustomerGrowthScenarioInput[];
  } = {},
): Map<string, ProjectionSimulationState> {
  const capacityGroups = options.capacityGroups ?? [];
  const capacityEvents = options.capacityEvents ?? [];
  const growthScenarios = options.growthScenarios ?? [];

  const states = new Map<string, ProjectionSimulationState>();
  const groupTotals = new Map<string, number>();

  projections.forEach((projection, index) => {
    const key = projectionKey(projection, index);
    const initial = projection.initialCustomers ?? 0;
    states.set(key, {
      customers: initial,
      constrained: false,
      grossAcquisitions: 0,
      churned: 0,
    });
    if (projection.capacityGroupId) {
      groupTotals.set(
        projection.capacityGroupId,
        (groupTotals.get(projection.capacityGroupId) ?? 0) + initial,
      );
    }
  });

  if (monthsElapsed <= 0) return states;

  for (let month = 1; month <= monthsElapsed; month++) {
    const referenceProjection = projections[0];
    const start = referenceProjection ? toDate(referenceProjection.startDate) : null;
    if (!start) break;
    const monthDate = new Date(start);
    monthDate.setUTCMonth(monthDate.getUTCMonth() + month);

    const pending: Array<{
      key: string;
      projection: CustomerProjectionInput;
      index: number;
      churned: number;
      planned: number;
      scenario?: CustomerGrowthScenarioInput;
    }> = [];

    projections.forEach((projection, index) => {
      const key = projectionKey(projection, index);
      const state = states.get(key);
      if (!state || !isProjectionActive(projection, monthDate)) return;

      const projectionStart = toDate(projection.startDate);
      if (!projectionStart) return;
      const projectionMonths = getMonthsElapsed(projectionStart, monthDate);
      if (projectionMonths < 0) return;

      const churnRate = projection.churnRateMonthly ?? 0;
      const churned = state.customers * churnRate;

      if (projection.projectionType === 'custom_timeline') {
        const target = customersFromCustomTimeline(projection, projectionMonths);
        const grossNeeded = Math.max(0, target - (state.customers - churned));
        pending.push({
          key,
          projection,
          index,
          churned,
          planned: grossNeeded,
        });
        return;
      }

      const scenario = findGrowthScenario(projection, growthScenarios, monthDate);
      const saturationPoint = resolveSaturationPoint(scenario);
      const planned = applySaturationMultiplier(
        plannedGrossAcquisitions(projection, state.customers, projectionMonths),
        state.customers,
        saturationPoint,
        scenario?.saturationSlowdownRate,
      );

      pending.push({
        key,
        projection,
        index,
        churned,
        planned,
        scenario,
      });
    });

    for (const item of pending) {
      const state = states.get(item.key)!;
      let allowed = item.planned;

      const projectionMax = getEffectiveProjectionMax(
        item.projection,
        monthDate,
        capacityEvents,
      );
      if (projectionMax !== undefined) {
        allowed = Math.min(allowed, Math.max(0, projectionMax - state.customers));
      }

      if (item.projection.maxMonthlyAcquisitions !== undefined) {
        allowed = Math.min(allowed, item.projection.maxMonthlyAcquisitions);
      }

      if (item.projection.capacityGroupId) {
        const group = capacityGroups.find(
          (entry) => entry.id === item.projection.capacityGroupId,
        );
        if (group) {
          const effectiveMax = getEffectiveGroupMax(group, monthDate, capacityEvents);
          const groupTotal = groupTotals.get(group.id) ?? 0;
          allowed = Math.min(allowed, Math.max(0, effectiveMax - groupTotal));
        }
      }

      if (allowed < item.planned - 1e-9) {
        state.constrained = true;
      }

      const acquired = Math.max(0, allowed);
      const nextCustomers = Math.max(0, state.customers - item.churned + acquired);
      const previousCustomers = state.customers;

      if (item.projection.capacityGroupId) {
        const groupId = item.projection.capacityGroupId;
        const currentGroupTotal = groupTotals.get(groupId) ?? 0;
        groupTotals.set(
          groupId,
          currentGroupTotal - previousCustomers + nextCustomers,
        );
      }

      state.customers = Math.round(nextCustomers);
      state.grossAcquisitions = acquired;
      state.churned = item.churned;
    }
  }

  return states;
}

export function getProjectionKey(
  projection: CustomerProjectionInput,
  index: number,
): string {
  return projectionKey(projection, index);
}

export function calculateEquilibriumCustomers(
  projection: CustomerProjectionInput,
): number | null {
  const churn = projection.churnRateMonthly ?? 0;
  if (churn <= 0) return null;

  if (projection.projectionType === 'fixed_monthly') {
    return (projection.monthlyAcquisitionTarget ?? 0) / churn;
  }

  if (
    projection.projectionType === 'growth_rate' &&
    (projection.monthlyGrowthRate ?? 0) === 0
  ) {
    return (projection.monthlyAcquisitionTarget ?? 0) / churn;
  }

  return null;
}
